import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Video, MapPin, Users, ExternalLink, LayoutGrid, List, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import { getInterviewSchedule } from '../api';

interface Props {
  currentUserId?: string;
  onNavigateCandidate?: (id: string) => void;
}

// 周视图网格配置
const DAY_START = 8;   // 起始小时
const DAY_END = 22;    // 结束小时
const HOUR_H = 44;     // 每小时像素高度
const GRID_H = (DAY_END - DAY_START) * HOUR_H;
const MIN_COL_W = 110; // 单列最小宽度
const WEEK_LABELS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];

// ===== 日期工具（均按北京时间处理，与后端 scheduled_time 保持一致）=====
function bjTodayStr() {
  return new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);
}
function parseDate(s: string) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}
function fmtDate(d: Date) {
  return d.toISOString().slice(0, 10);
}
function addDays(s: string, n: number) {
  const d = parseDate(s);
  d.setUTCDate(d.getUTCDate() + n);
  return fmtDate(d);
}
function mondayOf(s: string) {
  const dow = parseDate(s).getUTCDay() || 7;
  return addDays(s, -(dow - 1));
}

// ===== 单日事件布局：重叠面试自动分道并排（周视图用）=====
interface LaidOutEvent {
  iv: any;
  startMin: number;
  dur: number;
  lane: number;
  lanes: number;
}

function startMinuteOf(iv: any) {
  const t = iv.scheduled_time || '';
  return (parseInt(t.slice(11, 13), 10) || 0) * 60 + (parseInt(t.slice(14, 16), 10) || 0);
}

function layoutDay(evs: any[]): LaidOutEvent[] {
  const items = evs.map(iv => ({ iv, startMin: startMinuteOf(iv), dur: iv.duration_minutes || 45 }))
    .sort((a, b) => a.startMin - b.startMin);

  const clusters: typeof items[] = [];
  let cur: typeof items = [];
  let curEnd = -1;
  for (const it of items) {
    if (cur.length && it.startMin >= curEnd) {
      clusters.push(cur);
      cur = [];
      curEnd = -1;
    }
    cur.push(it);
    curEnd = Math.max(curEnd, it.startMin + it.dur);
  }
  if (cur.length) clusters.push(cur);

  const out: LaidOutEvent[] = [];
  for (const cluster of clusters) {
    const laneEnds: number[] = [];
    const base = out.length;
    for (const it of cluster) {
      let lane = laneEnds.findIndex(end => end <= it.startMin);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(0);
      }
      laneEnds[lane] = it.startMin + it.dur;
      out.push({ ...it, lane, lanes: 1 });
    }
    for (let i = base; i < out.length; i++) out[i].lanes = laneEnds.length;
  }
  return out;
}

function roundBadgeClass(round?: string) {
  if (round?.includes('终')) return 'bg-gray-50 text-gray-600';
  if (round?.includes('二') || round?.includes('复')) return 'bg-primary-50 text-primary-600';
  return 'bg-gray-100 text-gray-600';
}

// 面试官飞书日程回执：decline / 不在参与者里 = 他日历上根本看不到这场面试，属于得尽快处理的硬问题；
// needs_action 只是未点接受，日历上是能看到的，弱提醒即可。两者不能混为一谈。
function rsvpAlert(iv: any): { text: string; title: string } | null {
  const gone = [...(iv.rsvp_declined || []), ...(iv.rsvp_missing || [])];
  if (gone.length) {
    const parts: string[] = [];
    if (iv.rsvp_declined?.length) parts.push(`${iv.rsvp_declined.join('、')} 已拒绝该日程`);
    if (iv.rsvp_missing?.length) parts.push(`${iv.rsvp_missing.join('、')} 不在日程参与人中`);
    return {
      text: `${gone.join('、')} 日历上无此面试`,
      title: `${parts.join('；')}。飞书不会在他的日历上显示这场面试，建议重新发邀请或当面确认。`,
    };
  }
  return null;
}

function rsvpPendingText(iv: any): string | null {
  return iv.rsvp_pending?.length ? `${iv.rsvp_pending.join('、')} 未确认` : null;
}

export default function InterviewCalendar({ currentUserId, onNavigateCandidate }: Props) {
  const today = bjTodayStr();
  const [mode, setMode] = useState<'day' | 'week'>('day');
  const [selectedDay, setSelectedDay] = useState(today);
  const [onlyMine, setOnlyMine] = useState(false);
  const [raw, setRaw] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const gridRef = useRef<HTMLDivElement>(null);

  // 当前展示的一周（周一 ~ 周日）
  const weekStart = mondayOf(selectedDay);
  const weekEnd = addDays(weekStart, 6);

  // 每分钟刷新当前时间线
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  // 按周拉取数据
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getInterviewSchedule(weekStart, weekEnd)
      .then(d => { if (!cancelled) setRaw(d.interviews || []); })
      .catch(() => { if (!cancelled) setRaw([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [weekStart, weekEnd]);

  // 「仅看我参与的」过滤
  const interviews = useMemo(() => {
    if (!onlyMine || !currentUserId) return raw;
    return raw.filter(iv => (iv.interviewer_ids || '').includes(currentUserId));
  }, [raw, onlyMine, currentUserId]);

  const days = useMemo(() => {
    const list: string[] = [];
    for (let i = 0; i < 7; i++) list.push(addDays(weekStart, i));
    return list;
  }, [weekStart]);

  const byDay = useMemo(() => {
    const map: Record<string, any[]> = {};
    interviews.forEach(iv => {
      const date = (iv.scheduled_time || '').slice(0, 10);
      if (!map[date]) map[date] = [];
      map[date].push(iv);
    });
    return map;
  }, [interviews]);

  // 当前时间（北京时间分钟数）
  const bjNowDate = new Date(now + 8 * 3600 * 1000);
  const nowMin = bjNowDate.getUTCHours() * 60 + bjNowDate.getUTCMinutes();

  // 周视图打开时自动滚动到当前时间附近
  useEffect(() => {
    if (mode !== 'week' || !gridRef.current) return;
    if (days.includes(today) && nowMin > DAY_START * 60) {
      gridRef.current.scrollTop = Math.max(0, ((nowMin - DAY_START * 60) / 60) * HOUR_H - 100);
    } else {
      gridRef.current.scrollTop = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, weekStart, loading]);

  const goPrevWeek = () => setSelectedDay(addDays(selectedDay, -7));
  const goNextWeek = () => setSelectedDay(addDays(selectedDay, 7));
  const goToday = () => setSelectedDay(today);
  const isTodaySelected = selectedDay === today;

  const dayEvents = useMemo(
    () => (byDay[selectedDay] || []).slice().sort((a, b) => startMinuteOf(a) - startMinuteOf(b)),
    [byDay, selectedDay]
  );

  const scopeCount = mode === 'day' ? dayEvents.length : interviews.length;
  const hours: number[] = [];
  for (let h = DAY_START; h <= DAY_END; h++) hours.push(h);

  return (
    <div className="h-full flex flex-col bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* ===== 头部工具栏 ===== */}
      <div className="shrink-0 px-4 py-3 flex items-center justify-between gap-2 border-b border-gray-100">
        <div className="flex items-center gap-2 min-w-0">
          <CalendarIcon size={16} className="text-primary-500 shrink-0" />
          <h2 className="text-sm font-bold text-gray-900 shrink-0">面试日程</h2>
          <span className="text-xs text-gray-500 shrink-0">{scopeCount} 场</span>
          {loading && <div className="animate-spin rounded-full h-3 w-3 border-2 border-primary-400 border-t-transparent shrink-0" />}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <label className="hidden sm:flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
            <input type="checkbox" checked={onlyMine} onChange={e => setOnlyMine(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-3.5 h-3.5" />
            仅看我参与的
          </label>
          <input type="date" value={selectedDay} onChange={e => e.target.value && setSelectedDay(e.target.value)}
            title="跳转到指定日期"
            className="hidden md:block border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-600 focus:outline-none focus:border-primary-400" />
          {/* 视图切换 */}
          <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden">
            <button onClick={() => setMode('day')} title="日程视图"
              className={clsx('px-2 py-1 flex items-center gap-1 text-xs transition-colors',
                mode === 'day' ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-500 hover:bg-gray-50')}>
              <List size={12} /><span className="hidden lg:inline">日程</span>
            </button>
            <button onClick={() => setMode('week')} title="周日历视图"
              className={clsx('px-2 py-1 flex items-center gap-1 text-xs border-l border-gray-200 transition-colors',
                mode === 'week' ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-500 hover:bg-gray-50')}>
              <LayoutGrid size={12} /><span className="hidden lg:inline">日历</span>
            </button>
          </div>
          <button onClick={goToday} disabled={isTodaySelected}
            className={clsx('px-2.5 py-1 rounded-lg border text-xs font-medium transition-colors whitespace-nowrap',
              isTodaySelected ? 'border-gray-100 text-gray-300 cursor-default' : 'border-gray-200 text-gray-600 hover:bg-gray-50')}>
            回到今天
          </button>
        </div>
      </div>

      {/* ===== 周日期条 ===== */}
      <div className="shrink-0 flex items-center border-b border-gray-100 bg-gray-50/40">
        <button onClick={goPrevWeek} title="上一周"
          className="shrink-0 px-2 py-3 text-gray-400 hover:text-primary-600 transition-colors">
          <ChevronLeft size={16} />
        </button>
        <div className="flex-1 flex">
          {days.map(day => {
            const dow = parseDate(day).getUTCDay();
            const isSel = day === selectedDay;
            const isToday = day === today;
            const count = byDay[day]?.length || 0;
            return (
              <button key={day} onClick={() => setSelectedDay(day)}
                className="flex-1 flex flex-col items-center py-2 group">
                <span className={clsx('text-xs leading-4',
                  isSel ? 'text-primary-600 font-semibold' : dow === 0 || dow === 6 ? 'text-gray-400' : 'text-gray-500')}>
                  {WEEK_LABELS[dow]}
                </span>
                <span className={clsx('mt-1 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold transition-colors',
                  isSel ? 'bg-primary-600 text-white'
                    : isToday ? 'text-primary-600 ring-1 ring-primary-300 bg-primary-50'
                    : 'text-gray-900 group-hover:bg-gray-100')}>
                  {isToday && !isSel ? '今' : parseInt(day.slice(8, 10), 10)}
                </span>
                <span className={clsx('h-4 text-[11px] leading-4 font-medium',
                  count === 0 ? 'text-transparent' : isSel ? 'text-primary-600' : 'text-gray-500')}>
                  {count > 0 ? `${count}场` : '·'}
                </span>
              </button>
            );
          })}
        </div>
        <button onClick={goNextWeek} title="下一周"
          className="shrink-0 px-2 py-3 text-gray-400 hover:text-primary-600 transition-colors">
          <ChevronRight size={16} />
        </button>
      </div>

      {/* ===== 主体 ===== */}
      {mode === 'day' ? (
        <>
          {/* 当日标题 */}
          <div className="shrink-0 px-4 py-2 flex items-center gap-2 bg-gray-50/70 border-b border-gray-100">
            <span className={clsx('text-sm font-bold', isTodaySelected ? 'text-primary-600' : 'text-gray-700')}>
              {selectedDay.slice(5)} {WEEK_LABELS[parseDate(selectedDay).getUTCDay()]}
              {isTodaySelected && ' · 今天'}
            </span>
            <span className="text-xs text-gray-500">{dayEvents.length} 场面试</span>
          </div>

          {/* 当日时间轴 */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {dayEvents.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center py-12 text-gray-300">
                <CalendarIcon size={30} strokeWidth={1} className="mb-2" />
                <p className="text-xs">当天暂无面试安排</p>
              </div>
            ) : (
              <div className="py-1">
                {dayEvents.map((iv: any) => {
                  const hasLink = iv.location && /^https?:\/\//.test(iv.location);
                  const isCancelled = iv.status === 'cancelled';
                  const isCompleted = iv.status === 'completed';
                  const alert = rsvpAlert(iv);
                  const pendingText = rsvpPendingText(iv);
                  return (
                    <div key={iv.id} className="flex gap-3 px-4 py-3 hover:bg-gray-50/70 transition-colors">
                      {/* 时间列 */}
                      <div className="w-12 shrink-0 text-right pt-0.5">
                        <div className={clsx('text-sm font-bold font-mono leading-tight',
                          isCancelled ? 'text-gray-400 line-through' : 'text-gray-900')}>
                          {(iv.scheduled_time || '').slice(11, 16)}
                        </div>
                        <div className="text-[11px] text-gray-400 leading-tight mt-0.5">{iv.duration_minutes || 45}min</div>
                      </div>
                      {/* 时间轴轨道 */}
                      <div className="shrink-0 w-2.5 relative flex justify-center">
                        <span className="w-px bg-gray-200 h-full" />
                        <span className={clsx('absolute top-1.5 w-2 h-2 rounded-full ring-2 ring-white',
                          isCancelled ? 'bg-gray-300' : isCompleted ? 'bg-gray-400' : 'bg-primary-500')} />
                      </div>
                      {/* 内容：轮次、姓名、岗位、会议/地点、面试官同一行 */}
                      <div className="flex-1 min-w-0 pt-0.5 flex items-center gap-x-2.5 gap-y-1 flex-wrap">
                        <div className="flex items-center gap-1.5 cursor-pointer min-w-0"
                          onClick={() => onNavigateCandidate?.(iv.candidate_id)}>
                          <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0', roundBadgeClass(iv.round))}>
                            {iv.round}
                          </span>
                          <span className={clsx('text-sm font-bold hover:text-primary-600 transition-colors',
                            isCancelled ? 'text-gray-400 line-through' : 'text-gray-900')}>
                            {iv.candidate_name}
                          </span>
                          {iv.position_name && (
                            <span className="text-[11px] text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded-full truncate max-w-[150px]">
                              {iv.position_name}
                            </span>
                          )}
                          {isCancelled && <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">已取消</span>}
                          {isCompleted && <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">已结束</span>}
                        </div>
                        <div className="flex items-center text-xs text-gray-500">
                          {iv.interview_type === 'video' ? (
                            hasLink ? (
                              <a href={iv.location} target="_blank" rel="noopener noreferrer"
                                title={`点击进入视频会议室：${iv.location}`}
                                className="inline-flex items-center gap-1 bg-primary-50 text-primary-700 border border-primary-200 rounded-md px-2 py-0.5 font-medium hover:bg-primary-100 hover:border-primary-300 transition-colors shrink-0">
                                <Video size={12} />进入会议<ExternalLink size={10} />
                              </a>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-gray-500 shrink-0">
                                <Video size={12} className="text-gray-400" />视频面试
                                {iv.location && iv.location !== '待定' && (
                                  <span className="truncate max-w-[180px]">· {iv.location}</span>
                                )}
                              </span>
                            )
                          ) : (
                            <span className="inline-flex items-center gap-1 truncate max-w-[240px]">
                              <MapPin size={12} className="shrink-0" />{iv.location || '现场面试'}
                            </span>
                          )}
                        </div>
                        {alert && (
                          <span title={alert.title}
                            className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-600 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded shrink-0">
                            <AlertTriangle size={10} className="shrink-0" />{alert.text}
                          </span>
                        )}
                        {!alert && pendingText && (
                          <span title="面试官还没在飞书日程上点「接受」，日程在他日历上是可见的，仅供参考"
                            className="inline-flex items-center gap-1 text-[10px] text-gray-700 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded shrink-0">
                            {pendingText}
                          </span>
                        )}
                        {iv.interviewer_names?.length > 0 && (
                          <span className="inline-flex items-center gap-1 min-w-0 truncate ml-auto pl-3 text-xs text-gray-500">
                            <Users size={12} className="shrink-0" />{iv.interviewer_names.join('、')}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      ) : (
        /* ===== 周日历网格 ===== */
        <div className="flex-1 min-h-0 overflow-auto" ref={gridRef}>
          <div style={{ minWidth: 44 + 7 * MIN_COL_W }}>
            <div className="flex relative" style={{ height: GRID_H }}>
              {/* 左侧时间轴 */}
              <div className="w-11 shrink-0 relative bg-white sticky left-0 z-20">
                {hours.map(h => (
                  <div key={h} className="absolute right-1.5 text-[10px] text-gray-300 font-mono"
                    style={{ top: (h - DAY_START) * HOUR_H + 2 }}>
                    {h}:00
                  </div>
                ))}
              </div>

              {days.map(day => {
                const dow = parseDate(day).getUTCDay();
                const isToday = day === today;
                const events = layoutDay(byDay[day] || []);
                return (
                  <div key={day}
                    className={clsx('flex-1 relative border-l border-gray-100',
                      (dow === 0 || dow === 6) && 'bg-gray-50/60',
                      isToday && 'bg-primary-50/25')}>
                    {hours.map(h => (
                      <div key={h} className="absolute left-0 right-0 border-t border-gray-100"
                        style={{ top: (h - DAY_START) * HOUR_H }} />
                    ))}

                    {/* 当前时间线 */}
                    {isToday && nowMin >= DAY_START * 60 && nowMin <= DAY_END * 60 && (
                      <div className="absolute left-0 right-0 z-20 pointer-events-none"
                        style={{ top: ((nowMin - DAY_START * 60) / 60) * HOUR_H }}>
                        <div className="border-t-[1.5px] border-gray-500 relative">
                          <span className="absolute -left-[3px] -top-[3.5px] w-[7px] h-[7px] rounded-full bg-gray-500" />
                        </div>
                      </div>
                    )}

                    {events.map(({ iv, startMin, dur, lane, lanes }) => {
                      const top = Math.max(0, ((startMin - DAY_START * 60) / 60) * HOUR_H);
                      const height = Math.max(24, Math.min((dur / 60) * HOUR_H - 2, GRID_H - top - 1));
                      const widthPct = 100 / lanes;
                      const isCancelled = iv.status === 'cancelled';
                      const isCompleted = iv.status === 'completed';
                      const alert = rsvpAlert(iv);
                      return (
                        <div key={iv.id}
                          onClick={() => onNavigateCandidate?.(iv.candidate_id)}
                          title={`${(iv.scheduled_time || '').slice(11, 16)} ${iv.candidate_name || ''} ${iv.round || ''}${iv.position_name ? ' · ' + iv.position_name : ''}${iv.interviewer_names?.length ? '\n面试官: ' + iv.interviewer_names.join('、') : ''}${iv.interview_type === 'video' && iv.location && /^https?:\/\//.test(iv.location) ? '\n视频会议: ' + iv.location : ''}${alert ? '\n⚠️ ' + alert.title : ''}`}
                          className={clsx('absolute rounded-md border-l-[3px] px-1.5 py-0.5 overflow-hidden cursor-pointer transition-colors z-10',
                            isCancelled ? 'bg-gray-100 border-gray-300 opacity-70' :
                            isCompleted ? 'bg-gray-50 border-gray-400 hover:bg-gray-100' :
                            'bg-primary-50 border-primary-500 hover:bg-primary-100')}
                          style={{
                            top,
                            height,
                            left: `calc(${lane * widthPct}% + 2px)`,
                            width: `calc(${widthPct}% - 4px)`,
                          }}>
                          <div className="flex items-center gap-1 text-[10px] leading-tight">
                            <span className="font-mono text-gray-500">{(iv.scheduled_time || '').slice(11, 16)}</span>
                            {iv.interview_type === 'video'
                              ? <Video size={9} className={clsx('shrink-0', iv.location && /^https?:\/\//.test(iv.location) ? 'text-primary-500' : 'text-gray-400')} />
                              : <MapPin size={9} className="text-gray-400 shrink-0" />}
                            {alert && <AlertTriangle size={9} className="text-gray-500 shrink-0 ml-auto" />}
                          </div>
                          <div className={clsx('text-[11px] font-semibold leading-tight truncate',
                            isCancelled ? 'line-through text-gray-400' : 'text-gray-800')}>
                            {iv.candidate_name}
                          </div>
                          {height >= 44 && (
                            <div className="text-[10px] text-gray-400 truncate leading-tight">
                              {iv.round}{iv.position_name ? ` · ${iv.position_name}` : ''}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}

              {!loading && interviews.length === 0 && (
                <div className="absolute inset-x-0 top-16 text-center text-xs text-gray-300 pointer-events-none z-10">
                  本周暂无面试安排
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
